import { isNativeApp } from "@/lib/native";

/**
 * Native-only feature initialisers. All are no-ops on the web build (guarded by
 * isNativeApp()), and the Capacitor plugins are dynamically imported so the web
 * bundle never eagerly loads native code.
 *
 * On-device behaviour (push delivery, deep-link routing) must be verified on a
 * real device/emulator — it cannot be exercised in jsdom or the web preview.
 */

/** Hide the native splash once the web app has mounted (snappier than waiting
 *  for launchAutoHide). Safe: if this never runs, launchAutoHide removes it. */
export async function hideNativeSplash(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch (e) {
    console.warn("[native-splash] hide failed", e);
  }
}

/** Register for FCM/APNs push and log the device token (persist once FCM is configured). */
export async function initNativePush(): Promise<void> {
  if (!isNativeApp()) return;
  // IMPORTANT: PushNotifications.register() requires FCM to be configured
  // (google-services.json on Android / APNs on iOS). Without it, register()
  // throws a *native* fatal exception ("Default FirebaseApp is not initialized")
  // that JavaScript cannot catch — it crashes the app immediately after the
  // splash screen. So push stays OFF until FCM is wired up: build with
  // VITE_ENABLE_PUSH=true (and add google-services.json) to turn it on.
  if (import.meta.env.VITE_ENABLE_PUSH !== "true") {
    console.info("[native-push] disabled (set VITE_ENABLE_PUSH=true after configuring FCM)");
    return;
  }
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;
    await PushNotifications.register();
    PushNotifications.addListener("registration", (token) => {
      // FCM (Android) / APNs (iOS) token. Wire to a device-token endpoint when
      // FCM_SERVER_KEY / APNs are configured (see deferred edge-function secrets).
      console.info("[native-push] device token registered", token.value);
    });
    PushNotifications.addListener("registrationError", (err) => {
      console.warn("[native-push] registration error", err);
    });
  } catch (e) {
    console.warn("[native-push] init failed", e);
  }
}

/**
 * Route Supabase auth deep links (email confirm / password reset) back into the
 * app. The custom scheme equals the appId, e.g.
 *   com.valuegiftvouchers.consumer://reset-callback?code=...        (PKCE, default)
 *   com.valuegiftvouchers.consumer://login-callback#access_token=…  (implicit)
 * Host disambiguates: `reset-callback` → password-reset screen; else → portal home.
 */
export async function initDeepLinks(navigate: (path: string) => void): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("appUrlOpen", async ({ url }) => {
      try {
        const u = new URL(url);
        const isRecovery = u.host === "reset-callback" || u.searchParams.get("type") === "recovery" || /type=recovery/.test(u.hash);
        const { supabase } = await import("@/integrations/supabase/client");

        const code = u.searchParams.get("code");
        if (code) {
          // PKCE flow (supabase-js default): exchange the auth code for a session.
          await supabase.auth.exchangeCodeForSession(code);
        } else if (u.hash.includes("access_token")) {
          // Implicit flow: tokens arrive in the URL fragment.
          const h = new URLSearchParams(u.hash.replace(/^#/, ""));
          const access_token = h.get("access_token");
          const refresh_token = h.get("refresh_token");
          if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
        }
        navigate(isRecovery ? "/reset-password?recovery=1" : "/");
      } catch (e) {
        console.warn("[native-deeplinks] handling failed", e);
      }
    });
  } catch (e) {
    console.warn("[native-deeplinks] init failed", e);
  }
}
